import type { JSX } from 'react';
import {
  parseConversationMarkdown,
  type MarkdownBlock,
  type MarkdownInline,
} from '@term-deck/core/conversation-markdown.js';

/**
 * M488.3 — o corpo de uma mensagem da conversa, montado a partir da árvore tipada do core.
 *
 * O texto nunca vira HTML: cada nó vira um elemento React. Não existe `dangerouslySetInnerHTML` aqui, e é
 * essa ausência — não um sanitizador — que fecha a superfície de XSS. HTML embutido no Markdown chega como
 * texto, porque o parser do core não o interpreta.
 *
 * **A busca é requisito, não bônus.** A conversa do MASTER tem busca com destaque e contagem de resultados;
 * trocar string plana por árvore sem levar o destaque junto seria a regressão que este lote promete não
 * causar. Por isso o `query` desce até cada nó de texto.
 */

function HighlightedText({ value, query }: { value: string; query: string }): JSX.Element {
  const needle = query.trim().toLocaleLowerCase('pt-BR');
  if (!needle) return <>{value}</>;
  const haystack = value.toLocaleLowerCase('pt-BR');
  const parts: JSX.Element[] = [];
  let cursor = 0;
  let hit = haystack.indexOf(needle);
  let key = 0;
  while (hit >= 0) {
    if (hit > cursor) parts.push(<span key={key++}>{value.slice(cursor, hit)}</span>);
    parts.push(<mark key={key++}>{value.slice(hit, hit + needle.length)}</mark>);
    cursor = hit + needle.length;
    hit = haystack.indexOf(needle, cursor);
  }
  if (cursor < value.length) parts.push(<span key={key++}>{value.slice(cursor)}</span>);
  return <>{parts}</>;
}

function Inline({ nodes, query }: { nodes: readonly MarkdownInline[]; query: string }): JSX.Element {
  return (
    <>
      {nodes.map((node, index) => {
        const key = `${node.type}-${index}`;
        if (node.type === 'text') return <HighlightedText key={key} value={node.value} query={query} />;
        if (node.type === 'code') {
          return (
            <code className="md-code-inline" key={key}>
              <HighlightedText value={node.value} query={query} />
            </code>
          );
        }
        if (node.type === 'strong') {
          return (
            <strong key={key}>
              <Inline nodes={node.children} query={query} />
            </strong>
          );
        }
        if (node.type === 'em') {
          return (
            <em key={key}>
              <Inline nodes={node.children} query={query} />
            </em>
          );
        }
        return (
          <a key={key} href={node.href} target="_blank" rel="noreferrer noopener">
            <Inline nodes={node.children} query={query} />
          </a>
        );
      })}
    </>
  );
}

function inlineLabel(nodes: readonly MarkdownInline[]): string {
  return nodes.map(node => node.type === 'text' || node.type === 'code' ? node.value : inlineLabel(node.children)).join('');
}

function Blocks({ blocks, query }: { blocks: readonly MarkdownBlock[]; query: string }): JSX.Element {
  return (
    <>
      {blocks.map((block, index) => {
        const key = `${block.type}-${index}`;
        switch (block.type) {
          case 'paragraph':
            return (
              <p key={key}>
                <Inline nodes={block.children} query={query} />
              </p>
            );
          case 'heading': {
            // O nível vem do parser já limitado a 1-6; a conversa rebaixa dois degraus para um `#` do
            // agente não competir com os títulos da própria interface.
            const Tag = `h${Math.min(6, block.level + 2)}` as 'h3' | 'h4' | 'h5' | 'h6';
            return (
              <Tag className="md-heading" key={key}>
                <Inline nodes={block.children} query={query} />
              </Tag>
            );
          }
          case 'code':
            return (
              <div className="md-code-block" key={key}>
                {block.lang ? <span className="md-code-lang">{block.lang}</span> : null}
                <pre>
                  <code>
                    <HighlightedText value={block.value} query={query} />
                  </code>
                </pre>
                {block.closed ? null : (
                  <span className="md-code-open" title="a mensagem foi cortada antes do fim do bloco">
                    bloco cortado
                  </span>
                )}
              </div>
            );
          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag className="md-list" key={key}>
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <Blocks blocks={item} query={query} />
                  </li>
                ))}
              </Tag>
            );
          }
          case 'quote':
            return (
              <blockquote className="md-quote" key={key}>
                <Blocks blocks={block.children} query={query} />
              </blockquote>
            );
          case 'table':
            return (
              // Rolagem própria: tabela larga não pode alargar a coluna da conversa.
              <div className="md-table-wrap" key={key}>
                <table className="md-table">
                  <thead>
                    <tr>
                      {block.header.map((cell, cellIndex) => (
                        <th scope="col" key={cellIndex}>
                          <Inline nodes={cell} query={query} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} data-label={inlineLabel(block.header[cellIndex] ?? [])}>
                            <Inline nodes={cell} query={query} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return <hr className="md-rule" key={key} />;
        }
      })}
    </>
  );
}

export function MarkdownMessage({ text, query, preserveLiteralMetadata = false }: { text: string; query: string; preserveLiteralMetadata?: boolean }): JSX.Element {
  // Only a terminal metadata envelope is suppressed; quoted examples inside code remain readable.
  const visible = preserveLiteralMetadata ? text : text.replace(/(?:^|\r?\n)<oai-mem-citation>\s*<citation_entries>[\s\S]*?<\/oai-mem-citation>\s*$/, '').trimEnd();
  const blocks = parseConversationMarkdown(visible);
  return (
    <div className="md-body">
      <Blocks blocks={blocks} query={query} />
    </div>
  );
}
